import { getLoadAgents, getCustomersOrTraders, getEPOSiteNames } from '../apiCalls';
import { getNewLoadStatuses, getNewLoadMovement } from '../../api/v2/load';
import { getEPOStatusesV2 } from '../../api/v2/epo';

const TYPE_TO_SEARCH_DELAY = 3000;

/** Maps GET /v2/newload/status (or legacy status payloads) to FilterableSelect options. */
export const mapStatusOptions = (statuses = []) => {
  if (!Array.isArray(statuses)) return [];
  return statuses.map((opt) => {
    if (typeof opt === 'string') return { id: opt, data: opt };
    const label = opt.status ?? opt.data;
    const id = opt.statusNumber ?? opt.id ?? label;
    return { id: id?.toString?.() ?? id, data: label };
  });
};

/** Maps GET /v2/newload/movement to FilterableSelect options (id = movementTypeId for v2 search). */
export const mapMovementOptions = (routes = []) => {
  if (!Array.isArray(routes)) return [];
  return routes.map((opt) => {
    if (typeof opt === 'string') return { id: opt, data: opt };
    const label = opt.data ?? opt.movementType ?? opt.label ?? '';
    const id = opt.movementTypeId ?? opt.id ?? label;
    return { id: id?.toString?.() ?? id, data: label };
  });
};

export const fetchDropdownOptions = async (isEPO, isEORIUser) => {
  // TODO: could consider putting these in a Promise.all - check with BE if this might strain the DB?
  const statuses = isEPO ? mapStatusOptions(await getEPOStatusesV2()) : mapStatusOptions(await getNewLoadStatuses());
  const agents = !isEPO ? await getLoadAgents() : [];
  const routes = mapMovementOptions(await getNewLoadMovement());
  const siteNames = isEPO && isEORIUser ? await getEPOSiteNames() : [];

  // options should be array of objects with id and data fields
  return {
    statuses,
    agents: agents.length ? agents.map((opt) => ({ id: opt.fullname, data: opt.fullname })) : [],
    routes,
    customerNames: [],
    customerEoris: [],
    siteNames: siteNames.length ? siteNames.map((opt) => ({ id: opt.siteName, data: opt.siteName })) : [],
    epoTypes: [
      { id: 0, data: 'Standard' },
      { id: 1, data: 'Priority' },
      { id: 2, data: 'Priority (TAD)' },
    ],
  };
};

export const getTypeToSearchTimeout = (searchTerm, field, setOptions, setIsLoading) => {
  const searchingByName = field === 'name';
  const searchingByEori = field === 'eori';

  const newTimeout = setTimeout(async () => {
    const data = await getCustomersOrTraders(searchingByName ? searchTerm : '', searchingByEori ? searchTerm : '', 0);

    setOptions((prev) => ({
      ...prev,
      ...(searchingByName && {
        customerNames: data.length ? data.map((opt) => ({ id: opt.companyName, data: opt.companyName })) : [],
      }),
      ...(searchingByEori && {
        customerEoris: data.length ? data.map((opt) => ({ id: opt.companyEori, data: opt.companyEori })) : [],
      }),
    }));

    setIsLoading(false);
  }, TYPE_TO_SEARCH_DELAY);

  return newTimeout;
};

const formatDateISO = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const addMonths = (dateStr, months) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return formatDateISO(d);
};

const getEndOfCurrentMonth = () => {
  const now = new Date();
  return formatDateISO(new Date(now.getFullYear(), now.getMonth() + 1, 0, 12));
};

export const getMaxDate = (date) => {
  if (!date) return null;
  return addMonths(date, 6);
};

export const getMinDate = (date) => {
  if (!date) return null;
  return addMonths(date, -6);
};

export const resolveToDateForFrom = (from, currentTo) => {
  if (!from) return currentTo ?? null;
  const maxTo = getMaxDate(from);
  const rangeEnd = maxTo > getEndOfCurrentMonth() ? getEndOfCurrentMonth() : maxTo;
  if (!currentTo || currentTo > maxTo) return rangeEnd;
  if (currentTo < from) return from;
  return currentTo;
};

/** When the user changes To, keep their To and move From into the nearest valid 6-month window. */
export const resolveFromDateForTo = (to, currentFrom) => {
  if (!to) return currentFrom ?? null;
  const minFrom = getMinDate(to);
  if (!currentFrom) return minFrom;
  if (getMaxDate(currentFrom) < to || currentFrom > to) return minFrom;
  if (currentFrom < minFrom) return minFrom;
  return currentFrom;
};

export const getDefaultCreatedDates = () => {
  const now = new Date();
  return {
    defaultCreatedTo: formatDateISO(new Date(now.getFullYear(), now.getMonth() + 1, 0, 12)),
    defaultCreatedFrom: formatDateISO(new Date(now.getFullYear(), now.getMonth() - 2, 1, 12)),
  };
};

/**
 * Returns true if any filter value is selected (non-empty).
 * @param {object} values - Filter values object
 * @param {object} options - { excludeWhen: (key, value) => boolean } - return true to exclude a key from the check
 */
export const hasAnyFilterSelected = (values, options = {}) => {
  if (!values || typeof values !== 'object') return false;
  const { excludeWhen } = options;
  return Object.entries(values).some(([key, value]) => {
    if (excludeWhen && excludeWhen(key, value)) return false;
    if (value == null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  });
};

/**
 * Minimum number of characters required before a free-text filter field can be applied.
 * Keyed by field name so it works across the NeeNor, Business Partner and Customer
 * Application filter forms (EORI is named `eori` on some forms and `eoriNumber` on others).
 */
export const FILTER_MIN_LENGTHS = {
  companyName: 3,
  customerName: 3,
  eori: 2,
  eoriNumber: 2,
};

/**
 * Returns a validation message when a free-text filter field has a value that is
 * non-empty but shorter than its minimum length, otherwise null.
 */
/** Returns true if CABIE load filter fields have min-length validation errors. */
export const hasCabieLoadFilterMinLengthErrors = (values) =>
  Boolean(getFilterMinLengthError('customerName', values?.customerName)) ||
  Boolean(getFilterMinLengthError('eoriNumber', values?.eoriNumber));

/** Returns true if unified EPO load filter fields have min-length validation errors. */
export const hasEpoLoadFilterMinLengthErrors = (values) =>
  Boolean(getFilterMinLengthError('customerName', values?.customerName));

export const getFilterMinLengthError = (field, value) => {
  const min = FILTER_MIN_LENGTHS[field];
  if (!min) return null;
  const trimmed = (value ?? '').toString().trim();
  if (trimmed.length > 0 && trimmed.length < min) {
    return `Enter at least ${min} characters`;
  }
  return null;
};

/** Returns true if any free-text filter field has a value below its minimum length. */
export const hasFilterMinLengthErrors = (values) =>
  Object.keys(FILTER_MIN_LENGTHS).some((field) => getFilterMinLengthError(field, values?.[field]));

/** Terminal load statuses: results must include those rows; do not apply "exclude completed & cancelled". */
export const shouldUncheckExcludeCompletedForStatus = (statusLabel) => {
  if (statusLabel == null) return false;
  const s = String(statusLabel).trim().toLowerCase();
  return s === 'completed' || s === 'cancelled' || s === 'canceled';
};

/**
 * FilterableSelect stores option `id` (e.g. EPO statusNumber), not the display `data` string.
 * Map id ΓåÆ data for exclude-completed logic.
 * @param {unknown} raw - value from filter state (id or label)
 * @param {{ id?: unknown, data?: string }[]} statusOptions - from fetchDropdownOptions statuses
 */
export const resolveStatusDataLabel = (raw, statusOptions = []) => {
  if (raw == null) return null;
  const opt = statusOptions.find((o) => o?.id?.toString?.() === raw?.toString?.());
  if (opt?.data != null && String(opt.data).trim() !== '') return opt.data;
  return raw;
};
